"use client"

import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select"
import { BASE_URL } from "@/config"
import { handleFormChange } from "@/utils/formChange"
import axios from "axios"
import { Loader2, Plus } from "lucide-react"
import { FC, FormEvent, useState } from "react"
import { useRefresh } from "react-admin"
import { toast } from "sonner"
import { useUserContext } from "@/context/UserContext"

interface Props {
    id: number;
    title: string;
    description?: string;
}

const ConfigurePrices: FC<Props> = ({ id, title, description }) => {

    const refresh = useRefresh();
    const { decodedToken } = useUserContext();

    const [loading, setLoading] = useState(false);

    const [formProp, setFormProp] = useState({
        tour_id: id,
        group_size: "",
        price: "",
        child_over_12_fee: "",
        child_under_12_fee: "",
        vehicle_type: ""
    })

    const [isOpen, setIsOpen] = useState(false);

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(true);
    }

    const submitForm = async (e: FormEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setLoading(true);
        await axios.post(`${BASE_URL}/tour-pricing`, formProp, {
            headers: {
                'Authorization': `Bearer ${window.localStorage.getItem("access_token")}`
            }
        }).then(() => {

            setTimeout(() => {
                toast('Price added successfully', {
                    description: 'Price for this tour was added successfully!',
                });
                setIsOpen(false);
                setLoading(false);
                refresh();
            }, 2000);

        }).catch(() => {
            toast('An error occured. Please try again')
            setLoading(false);
        })
    }

    return (
        <div>
            <Dialog
                open={isOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        setIsOpen(false);
                    }
                }}
            >
                <Button onClick={handleClick} disabled={decodedToken?.permissions.user_role !== 'owner'}>
                        <Plus className="h-4 w-4" />
                        {title}
                    </Button>
                <DialogContent className="sm:max-w-[900px]">
                    <DialogHeader className="mb-4">
                        <DialogTitle>{title}</DialogTitle>
                        {description && <DialogDescription>{description}</DialogDescription>}
                    </DialogHeader>
                    <form onSubmit={submitForm} className="pr-4 pl-4 max-w-3xl">
                        <div className="grid grid-cols-3 gap-4">
                            <div className="grid gap-1">
                                <Label htmlFor="vehicle_class" className="text-right mb-2">
                                    Select vehicle class *
                                </Label>
                                <Select
                                    value={formProp.vehicle_type}
                                    onValueChange={(value) => setFormProp((prev) => ({ ...prev, vehicle_type: value }))}
                                >
                                    <SelectTrigger className="w-[235px]">
                                        <SelectValue placeholder="Choose Class" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectGroup>
                                            <SelectLabel>Vehicle Class</SelectLabel>
                                            <SelectItem value="suv">SUV</SelectItem>
                                            <SelectItem value="van">VAN</SelectItem>
                                            <SelectItem value="bus">BUS</SelectItem>
                                        </SelectGroup>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid gap-1">
                                <Label htmlFor="group_size" className="text-right mb-2">
                                    Group Size *
                                </Label>
                                <Input id="group_size" name="group_size" type="number" value={formProp.group_size}
                                    onChange={(e: FormEvent<HTMLInputElement>) => handleFormChange(e, setFormProp)} placeholder="Group Size" required />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4 mt-5">
                            <div className="grid gap-1">
                                <Label htmlFor="price" className="text-right mb-2">
                                    Price *
                                </Label>
                                <Input id="price" name="price" type="number" value={formProp.price}
                                    onChange={(e: FormEvent<HTMLInputElement>) => handleFormChange(e, setFormProp)} placeholder="Price" required />
                            </div>
                            <div className="grid gap-1">
                                <Label htmlFor="child_over_12_fee" className="text-right mb-2">
                                    Child over 12 fee *
                                </Label>
                                <Input id="child_over_12_fee" name="child_over_12_fee" type="number" value={formProp.child_over_12_fee}
                                    onChange={(e: FormEvent<HTMLInputElement>) => handleFormChange(e, setFormProp)} placeholder="Child over 12 fee" required />
                            </div>
                            <div className="grid gap-1">
                                <Label htmlFor="child_under_12_fee" className="text-right mb-2">
                                    Child under 12 fee *
                                </Label>
                                <Input id="child_under_12_fee" name="child_under_12_fee" type="number" value={formProp.child_under_12_fee}
                                    onChange={(e: FormEvent<HTMLInputElement>) => handleFormChange(e, setFormProp)} placeholder="Child under 12 fee" required />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4 mt-8 pb-8">
                            <Button type="submit" disabled={loading}>
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    "Save changes"
                                )}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ConfigurePrices;